import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button } from '@mui/material';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';

import { getProfileInfo, updateProfile } from 'src/features/notifications/utils';
import { AppDispatch, RootState } from 'src/store/store';
import { updatePreference } from 'src/features/notifications/notificationsSlice';
import { getNotificationSections } from 'src/features/notifications/utils';
import { NotificationSectionsType } from 'src/features/notifications/constants';

import AddCellPhoneNumber from './modals/AddCellPhoneNumber';
import { Checkbox } from 'src/components/new/inputs/Checkbox';
import { Typography } from 'src/components/new/Typography';
import { setCurrentUser } from 'src/features/user/userSlice';

const NotificationsTab = () => {
  const dispatch: AppDispatch = useDispatch();

  const preferences = useSelector((state: RootState) => state.notifications.currentPreferences);
  const orgType = useSelector((state: RootState) => state.notifications.orgType);
  const role = useSelector((state: RootState) => state.notifications.role);

  const currentUser = useSelector((state: RootState) => state.user.currentUser);

  const [notificationSections, setNotificationSections] = useState<NotificationSectionsType[]>([]);
  const [showPhoneModal, setShowPhoneModal] = useState<boolean>(false);
  const [selectedPreference, setSelectedPreference] = useState<{
    keyPath: string;
    section: keyof typeof preferences | '';
  }>({ keyPath: '', section: '' });
  const previousPreferencesRef = useRef(preferences);

  useEffect(() => {
    dispatch(getProfileInfo());
  }, [dispatch]);

  useEffect(() => {
    if (orgType && role) {
      setNotificationSections(getNotificationSections(orgType, role));
    }
  }, [orgType, role]);

  useEffect(() => {
    // Call the updateProfile only if there are changes
    const preferencesChanged = JSON.stringify(previousPreferencesRef.current) !== JSON.stringify(preferences);

    if (preferencesChanged) {
      dispatch(updateProfile(role));
      dispatch(setCurrentUser({ user: { ...currentUser, notifications: preferences.personalActivity } }));
      previousPreferencesRef.current = preferences;
    }
  }, [preferences, dispatch]);

  const handleCheckboxChange = (
    section: keyof typeof preferences,
    keyPath: string,
    type: 'sms' | 'email',
    nestedKey?: string,
  ) => {
    if (type === 'sms' && (!currentUser.cellPhoneNumber || !currentUser.isSmsAllowed)) {
      setShowPhoneModal(true);
      setSelectedPreference({ keyPath, section });
      return;
    }

    if (nestedKey) {
      const currentValue = preferences[section]?.[keyPath]?.[nestedKey]?.[type] || false;

      dispatch(
        updatePreference({
          section,
          keyPath,
          type,
          value: !currentValue,
          nestedKey,
        }),
      );
    } else {
      const currentValue = preferences[section]?.[keyPath]?.[type] || false;

      dispatch(
        updatePreference({
          section,
          keyPath,
          type,
          value: !currentValue,
        }),
      );
    }
  };

  const setDefaultSMSPreferences = () => {
    if (selectedPreference.keyPath && selectedPreference.section) {
      dispatch(
        updatePreference({
          section: selectedPreference.section,
          keyPath: selectedPreference.keyPath,
          type: 'sms',
          value: true,
        }),
      );
    }
    
    dispatch(
      updatePreference({
        section: 'personalActivity',
        keyPath: 'jobOnHold',
        type: 'sms',
        value: true,
      }),
    );

    dispatch(
      updatePreference({
        section: 'personalActivity',
        keyPath: 'jobCompletion',
        type: 'sms',
        value: true,
      }),
    );

    if (role === 'admin') {
      dispatch(
        updatePreference({
          section: 'organizationActivity',
          keyPath: 'teammateRequests',
          type: 'sms',
          value: true,
        }),
      );
    }
  };

  return (
    <div
      style={{
        width: '35%',
        marginLeft: '1rem',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0rem 0.9375rem',
          borderRadius: '0.25rem',
          fontWeight: 'bold',
          mb: 2,
          mt: '1.5rem',
          color: '#333',
        }}
      >
        <Box sx={{ flex: 2 }}></Box>
        <Box sx={{ flex: 1, textAlign: 'center' }}>
          <Typography sx={{ fontSize: '0.875rem', lineHeight: '1.25rem', fontWeight: 500, color: '#1D1B20' }}>
            Email
          </Typography>
        </Box>
        <Box sx={{ flex: 1, textAlign: 'center' }}>
          <Typography sx={{ fontSize: '0.875rem', lineHeight: '1.25rem', fontWeight: 500, color: '#1D1B20' }}>SMS</Typography>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {notificationSections.map((section, sectionIndex) => (
          <Box key={sectionIndex}>
            <Typography
              variant="h6"
              sx={{ fontWeight: '500', mb: '1.5rem', fontSize: '1.375rem', lineHeight: '1.75rem', color: '#1D1B20' }}
            >
              {section.title}
            </Typography>
            <Box>
              {section.notifications.map((item, itemIndex) => {
                return (
                  <Box key={itemIndex} sx={{ display: 'flex', flexDirection: 'column', mb: 2 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '0rem 0.9375rem',
                        borderRadius: '0.25rem',
                      }}
                    >
                      <Box sx={{ flex: 2 }}>
                        <Typography sx={{ fontSize: '0.875rem', lineHeight: '1.25rem', fontWeight: 500, color: '#1D1B20' }}>
                          {item.label}
                        </Typography>
                        <Typography sx={{ fontSize: '0.75rem', lineHeight: '1rem', fontWeight: 400, color: '#1D1B20' }}>
                          {item.subText}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          flex: 1,
                          display: 'flex',
                          justifyContent: 'center',
                        }}
                      >
                        <Checkbox
                          checked={
                            // disabled items are already enabled by default.
                            preferences?.[section.type]?.[item.key]?.email || item.disabled || false
                          }
                          disabled={item.disabled}
                          onChange={() => handleCheckboxChange(section.type, item.key, 'email')}
                        />
                      </Box>
                      <Box
                        sx={{
                          flex: 1,
                          display: 'flex',
                          justifyContent: 'center',
                        }}
                      >
                        <Checkbox
                          checked={preferences?.[section.type]?.[item.key]?.sms || false}
                          onChange={() => handleCheckboxChange(section.type, item.key, 'sms')}
                        />
                      </Box>
                    </Box>
                    {item.nested && (
                      <Box sx={{ ml: 3, mt: 2 }}>
                        {item.nested.map((nestedItem: any, nestedIndex: number) => {
                          return (
                            <Box
                              key={nestedIndex}
                              sx={{
                                display: 'flex',
                                padding: '0.1875rem 0.9375rem',
                              }}
                            >
                              <Box sx={{ flex: 2 }}>
                                <Typography
                                  sx={{ fontSize: '0.875rem', lineHeight: '1.25rem', fontWeight: 500, color: '#1D1B20' }}
                                >
                                  {nestedItem.label}
                                </Typography>
                                <Typography
                                  sx={{ fontSize: '0.75rem', lineHeight: '1rem', fontWeight: 400, color: '#1D1B20' }}
                                >
                                  {nestedItem.subText}
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  flex: 1,
                                  display: 'flex',
                                  justifyContent: 'center',
                                  ml: -3,
                                }}
                              >
                                <Checkbox
                                  checked={preferences?.[section.type]?.[item.key]?.[nestedItem.key]?.email || false}
                                  onChange={() => handleCheckboxChange(section.type, item.key, 'email', nestedItem.key)}
                                />
                              </Box>
                              <Box
                                sx={{
                                  flex: 1,
                                  display: 'flex',
                                  justifyContent: 'center',
                                }}
                              ></Box>
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        ))}
        <Box sx={{ display: 'flex', flexDirection: 'column', mb: 2 }}>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            <Typography variant="h6" sx={{ flex: 1.5, fontWeight: '500' }}>
              Marketing
            </Typography>
            <Box
              sx={{
                flex: 3,
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Button
                target="_blank"
                rel="noreferrer"
                fullWidth
                href='https://info.certificateofservice.com/communications'
                variant="outlined"
                sx={{
                  background: '#fff',
                  color: '#000',
                  borderRadius: '0rem',
                  borderColor: '#d6d6d6',
                  textTransform: 'initial',
                }}
              >
                <ArrowOutwardIcon /> View Marketing Preferences
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
      {showPhoneModal && (
        <AddCellPhoneNumber
          open={showPhoneModal}
          onClose={() => {
            setShowPhoneModal(false);
          }}
          onSuccess={setDefaultSMSPreferences}
        />
      )}
    </div>
  );
};

export default NotificationsTab;
